import { ActivatedRoute } from '@angular/router';
import { Component, computed, signal } from '@angular/core';
import { CommonModule, NgForOf, NgIf } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { DividerModule } from 'primeng/divider';
import { MessagesModule } from 'primeng/messages';
import { TooltipModule } from 'primeng/tooltip';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { AssignCampaignNode } from '@modules/campaigns/Domain/Nodes/AssignCampaignNode';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { CampaignService } from '@modules/campaigns/services/campaign.service';
import { TreeSelectComponent } from '@shared/components/tree-select/tree-select.component';
import { TreeNode } from '@shared/components/tree-select/Domain/treeNode.model';
import { getValidationErrorMessage } from '@modules/campaigns/Domain/Enums/ValidationErrors';
import { WorkflowService } from '@modules/campaigns-new/Application/Services/workflow.service';
import { WorkflowType } from '@modules/campaigns-new/Domain/Enums/WorkflowType';
import { OrderCampaignsService } from '@shared/services/orderCampaigns.service';
import { PhasesService } from '@modules/campaigns/Application/Services/phases.service';
import { IPhase } from '@modules/customer-journey/Domain/phase.model';
import { ChipModule } from 'primeng/chip';

@Component({
  selector: 'app-assign-campaign-inspector',
  standalone: true,
  imports: [
    TranslateModule,
    DividerModule,
    MessagesModule,
    TooltipModule,
    TreeSelectComponent,
    NgIf,
    NgForOf,
    ChipModule,
    CommonModule,
    LucideIconsModule,
  ],
  templateUrl: './assign-campaign-inspector.component.html',
  styleUrls: ['./assign-campaign-inspector.component.scss'],
})
export class AssignCampaignInspectorComponent extends InspectorBase<AssignCampaignNode> {
  campaignOptions = signal<TreeNode[]>([]);
  selectedCampaignId = signal<string | null>(null);
  selectedCampaignDetails = signal<any>(null);

  phase = signal<IPhase | null>(null);
  phasesLoaded = signal(false);

  readonly totalNodeCount = computed(() => this.selectedCampaignDetails()?.nodes?.length ?? 0);
  readonly smsNodeCount = computed(
    () =>
      (this.selectedCampaignDetails()?.nodes ?? []).filter(
        (n: any) => n.parameters.name === 'sendSms' || n.parameters.name === 'sendCertifiedSMS',
      ).length,
  );
  readonly emailNodeCount = computed(
    () =>
      (this.selectedCampaignDetails()?.nodes ?? []).filter(
        (n: any) => n.parameters.name == 'sendEmail' || n.parameters.name == 'sendCertifiedEmail',
      ).length,
  );

  readonly translatedType = computed(() => {
    const type = this.selectedCampaignDetails()?.type as 'drip' | 'pill' | undefined;
    if (!type) return '';
    return type === WorkflowType.DRIP
      ? this.translate.instant('DATA.MODULES.CAMPAINGS.GENERIC_INFO.TYPE.DRIP_SIMPLE')
      : this.translate.instant('DATA.MODULES.CAMPAINGS.GENERIC_INFO.TYPE.PILL_SIMPLE');
  });

  constructor(
    private workflowState: WorkflowStateService,
    private campaignService: CampaignService,
    private translate: TranslateService,
    private campaignNewService: WorkflowService,
    private orderCampaignsService: OrderCampaignsService,
    private phasesService: PhasesService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  get nodeValue(): AssignCampaignNode | null {
    return this.node() as AssignCampaignNode;
  }

  getTabs(): { label: string; value: 'preview' | 'config' }[] {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.ASSIGN_CAMPAIGN.TABS.PREVIEW'),
        value: 'preview',
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.ASSIGN_CAMPAIGN.TABS.CONFIG'),
        value: 'config',
      },
    ];
  }

  protected refreshFromConfig() {
    const config = this.nodeValue?.config;
    if (config?.['targetCampaignId']) {
      this.selectedCampaignId.set(config['targetCampaignId'] as string);
      this.fetchCampaignDetails(config['targetCampaignId'] as string);
    } else {
      this.selectedCampaignId.set(null);
      this.selectedCampaignDetails.set(null);
    }
    this.loadCampaignOptions();
  }

  private loadCampaignOptions() {
    this.campaignNewService
      .getWorkflows([WorkflowType.DRIP, WorkflowType.PILL])
      .subscribe((campaigns: any[]) => {
        const treeNodes: TreeNode[] = this.orderCampaignsService.parseCampaignsToTree(
          campaigns,
          this.selectedCampaignId(),
        );
        this.campaignOptions.set(treeNodes);
      });
  }

  onCampaignSelectionChange(nodes: TreeNode[]) {
    if (nodes.length > 0) {
      const campaignId = nodes[0].key;
      this.selectedCampaignId.set(campaignId);
      this.fetchCampaignDetails(campaignId);
      this.scheduleApplyChanges();
      this.loadCampaignOptions();
    } else {
      this.selectedCampaignId.set(null);
      this.selectedCampaignDetails.set(null);
      this.scheduleApplyChanges();
      this.loadCampaignOptions();
    }
  }

  private fetchCampaignDetails(id: string) {
    this.campaignService.getCampaign(id).then((campaign) => {
      this.selectedCampaignDetails.set(campaign);
      this.loadPhasesAndResolve();
    });
  }

  private loadPhasesAndResolve() {
    const details = this.selectedCampaignDetails();
    if (!details || !details.bucket || this.phasesLoaded()) return;

    this.phasesService.getPhases().subscribe((res: any) => {
      const phases: IPhase[] = (res?.phases ?? []).map((p: any) => p);
      this.phasesLoaded.set(true);

      const bucket = details.bucket as { start: number; finish: number } | undefined;
      if (!bucket) return;

      const found = phases.find(
        (ph: any) => ph.bucket.finish >= bucket.finish && bucket.start >= ph.bucket.start,
      );
      this.phase.set(found ?? null);
    });
  }

  applyChanges() {
    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig = {
      ...node.config,
      targetCampaignId: this.selectedCampaignId(),
    };

    const updatedNode = new AssignCampaignNode(node.id, node.position, updatedConfig);
    updatedNode.parentNodes = [...node.parentNodes];
    updatedNode.childrenNodes = [...node.childrenNodes];

    const validationErrors = updatedNode.validate();
    this.errors.set(validationErrors);

    if (validationErrors.length > 0) return;
    this.workflowState.addOrUpdateNode(updatedNode);
  }

  getErrorMessage(error: any): string {
    return getValidationErrorMessage(error.code, error.params, this.translate);
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'published':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'paused':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'unpublished':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'draft':
      default:
        return 'bg-blue-50 text-blue-700 border-blue-200';
    }
  }

  viewCampaign() {
    const id = this.selectedCampaignId();
    if (!id) return;

    const tenantId = this.route.snapshot.queryParams['tenant'];
    const url = `/campaigns/edit/${id}${tenantId ? '?tenant=' + tenantId : ''}`;
    window.open(url, '_blank');
  }
}
